import { IoIosArrowBack } from "react-icons/io";
import { DataGrid } from '@mui/x-data-grid';
import { useState, useEffect } from "react";
import { collection, query, onSnapshot, where, writeBatch, doc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import Swal from 'sweetalert2';
import { backToTimestamp } from "../components/EditViolation";

export default function BulkEdit ( { setBulkCheck, user } ) {
    const [violations, setViolations] = useState([]);
    const [bulkValue, setBulkValue] = useState("");
    const [selectedIds, setSelectedIds] = useState([]);
    const [fineChangeAmount, setFineChangeAmount] = useState("");
    const [effectiveStartDate, setEffectiveStartDate] = useState("");

    const handleBulkUpdate = async (e) => {
        e.preventDefault();

        if (!effectiveStartDate || !bulkValue || !fineChangeAmount) {
            Swal.fire({
                icon: "error",
                title: "Missing Information",
                text: "Please fill in all fields before applying the bulk edit.",
                confirmButtonColor: "#3085d6",
            });
            return;
        }
        
        Swal.fire({
            title: 'Are you sure?',
            html: `This action will <strong>${bulkValue === "increase" ? "increase" : "decrease"}</strong> the fine amount for all selected violations by 
                <strong>$${fineChangeAmount}</strong> starting on <strong>${effectiveStartDate}</strong>. This cannot be undone.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, update all!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const start = effectiveStartDate;
                    let fine = fineChangeAmount;
        
                    if (selectedIds.length === 0) {
                        Swal.fire({
                            icon: "error",
                            title: "No Violations Selected",
                            text: "Please select at least one violation to apply the bulk edit.",
                            confirmButtonColor: "#3085d6",
                        });
                        return;
                    }

                    const batch = writeBatch(db);
        
                    selectedIds.forEach((id) => {
                        const violation = violations.find(v => v.id === id);
                        const currentFine = violation.fine;
                        const newFine = bulkValue === "increase" ? parseFloat(currentFine) + parseFloat(fine) : Math.max(0, parseFloat(currentFine) - parseFloat(fine));

                        const violationRef = doc(db, "violations", id);
                        batch.update(violationRef, {
                            effectiveStartDate: backToTimestamp(start),
                            fine: newFine,
                            modified_user: user,
                            modified_date: new Date(),
                        });
                    });
        
                    await batch.commit();
                    setBulkCheck(false);
                    Swal.fire({
                        toast: true,
                        position: 'bottom', 
                        icon: 'success',
                        title: 'Bulk Update Successful!',
                        showConfirmButton: false,
                        timer: 3000,
                        customClass: {
                            popup: 'swal-bottom-center'
                        }
                    });
                } catch (error) {
                    console.error("Error during bulk update:", error);
                    Swal.fire({
                        icon: "error",
                        title: "Bulk Update Failed",
                        text: "An error occurred while processing the bulk update. Please try again.",
                        confirmButtonColor: "#3085d6",
                    });
                }
            }
        });
    }

    useEffect(() => {
        const activeViolationsQuery = query(
            collection(db, "violations"),
                where("effectiveEndDate", "==", null)
        );
            
        const unsubscribe = onSnapshot(activeViolationsQuery, (querySnapshot) => {
            let violationsArray = querySnapshot.docs.map((doc) => ({
                ...doc.data(),
                id: doc.id,
        }));
        
            setViolations(violationsArray);
        });
    
        return () => unsubscribe();

    }, []);


    const localRows = violations.map((violation) => ({
        id: violation.id,
        arsCode: violation.arsCode,
    }));

    const localColumns = [
        { field: "arsCode", headerName: "ARS-Code", width: 300 },
    ];

    return (
        <div className="bulk-edit-background" >
            <div className="bulk-edit-back-button" onClick={() => setBulkCheck(false)}>
                <IoIosArrowBack className="close-bulk-edit" style={{width: "7vw", height: "40px", color: "#EFBF84", zIndex: 1000}}/>
            </div>
            <h3 style={{marginTop: "-30px", marginBottom: "18px"}}>Bulk Edit</h3>

            <div style={{ height: 400, width: '50%', margin: "0 auto"}} >
                <DataGrid rows={localRows} columns={localColumns} checkboxSelection onRowSelectionModelChange={(model) => {
                    if (model.type === "include") {
                        setSelectedIds(Array.from(model.ids));
                    } else {
                        // "exclude" means everything is selected
                        const allIds = localRows.map(row => row.id);
                        const excluded = new Set(model.ids);

                        const selected = allIds.filter(id => !excluded.has(id));
                        setSelectedIds(selected);
                    }
                }}/>

            </div>
        
            <form name="bulk-edit-violation-form" className="bulk-edit-violation-form" onSubmit={handleBulkUpdate}>
                <div className="form-row full-width-row">
                    <label htmlFor="effectiveStartDate">Effective Start Date:</label>
                    <input id="effectiveStartDate" type="date" title="Effective Start Date" required value={effectiveStartDate} onChange={(e) => setEffectiveStartDate(e.target.value)} />
                </div>

                <p style={{color: "red", fontSize: "11px"}}><strong>Note:</strong> Effective End Date will automatically be set to the day before selected Effective Start Date.</p>

                <div className="form-row full-width-row">
                    <label>Did the fines increase or decrease?</label>
                    <select name="fineChange" id="fineChange" title="Fine Change" required value={bulkValue} onChange={(e) => {setBulkValue(e.target.value);}}>
                        <option value="" disabled>Select an option</option>
                        <option value="increase">Increase</option>
                        <option value="decrease">Decrease</option>
                    </select>
                </div>
                
                {bulkValue === "increase" || bulkValue === "decrease" ? (
                    <div className="form-row full-width-row">
                        <label>How much did they {bulkValue} by?</label>
                        <input id="fineChangeAmount" type="number" placeholder="Fine Difference" value={fineChangeAmount} onChange={(e) => setFineChangeAmount(e.target.value)} title="Fine Change Amount" min="0"
                            onWheel={(e) => e.target.blur()}
                            required />
                    </div> ) : (<div></div>)
                }
                <br/>
                <button type="submit" className="save-bulk-edit-button" onClick={handleBulkUpdate}>Submit Bulk Edit</button>
            </form>
            
        </div>
    );


}